import { chargeSignMode } from '@shared/lib';
import { convertPartsMinor } from './split';

/**
 * "Assign each item" — the math behind the Breakdown's per-item member assignment, shared by the
 * AssignItemsModal's Grand-Total panel AND the bill's split so the two can never disagree.
 * Item/fee prices are the breakdown's raw strings (MAJOR units); shares are MAJOR floats until
 * `assignedSharesToSplit` turns them into an exact minor-unit split.
 */

/** A breakdown row as the assignment math needs it (structural: `BreakdownItem` satisfies it). */
export interface AssignableItem {
  readonly id: string;
  readonly name: string;
  readonly price: string;
  /** A charge's chosen sign — only meaningful for a free-sign charge ("Other"); see
   *  `chargeSignMode`. Absent ⇒ "+". */
  readonly sign?: '+' | '-' | undefined;
}

export interface ItemAssignment {
  itemId: string;
  /** 'each' = each assigned member pays the full item price; 'full' = price split equally. */
  splitType: 'each' | 'full';
  assignedMemberIds: string[];
}

/** One member's assigned amounts, MAJOR units. */
export interface AssignedShare {
  readonly items: number;
  readonly extras: number;
  readonly total: number;
}

const toNum = (v: string): number => Number.parseFloat(v) || 0;

/** "Discount" subtracts from the total; every other fee/tax type adds. */
export const isDiscountFee = (name: string): boolean => name.toLowerCase() === 'discount';

/**
 * A fee/tax row's SIGNED amount (MAJOR units), by its ROLE (`chargeSignMode`): a Discount always
 * subtracts its magnitude, a Tip/Tax always adds it, and a free-sign charge ("Other") follows the
 * row's own `sign` (the operator toggle; absent ⇒ adds). The price is entered as a plain
 * magnitude — the modal shows the operator separately — but a typed "-100" means the same. The
 * single rule every breakdown total (Itemization, Assign each item, the bill amount, the wire)
 * must use, so none can disagree.
 */
export function signedChargeAmount(name: string, price: string, sign?: '+' | '-'): number {
  const magnitude = Math.abs(toNum(price));
  switch (chargeSignMode(name)) {
    case 'minus':
      return -magnitude;
    case 'plus':
      return magnitude;
    default:
      return sign === '-' ? -magnitude : magnitude;
  }
}

/**
 * Per-member amounts for an assignment: each item's price goes to its assigned members (split
 * equally in 'full' mode, in full to each in 'each' mode), and the additional charges (fees −
 * discounts) are spread across members in proportion to how many item-slots each holds. Members
 * assigned to nothing are absent from the result.
 */
export function computeAssignedShares(
  items: readonly AssignableItem[],
  feesAndTaxes: readonly AssignableItem[],
  assignments: readonly ItemAssignment[],
): Record<string, AssignedShare> {
  const byItem = new Map(assignments.map((a) => [a.itemId, a]));
  const totalExtras = feesAndTaxes.reduce(
    (sum, fee) => sum + signedChargeAmount(fee.name, fee.price, fee.sign),
    0,
  );

  const itemsByMember: Record<string, number> = {};
  const slotsByMember: Record<string, number> = {};
  let totalSlots = 0;
  for (const item of items) {
    const assignment = byItem.get(item.id);
    const ids = assignment?.assignedMemberIds ?? [];
    if (ids.length === 0) continue;
    const price = toNum(item.price);
    const share = assignment?.splitType === 'each' ? price : price / ids.length;
    for (const id of ids) {
      itemsByMember[id] = (itemsByMember[id] ?? 0) + share;
      slotsByMember[id] = (slotsByMember[id] ?? 0) + 1;
      totalSlots += 1;
    }
  }

  const out: Record<string, AssignedShare> = {};
  for (const [id, itemsAmount] of Object.entries(itemsByMember)) {
    const extras = totalSlots === 0 ? 0 : (totalExtras * (slotsByMember[id] ?? 0)) / totalSlots;
    out[id] = { items: itemsAmount, extras, total: itemsAmount + extras };
  }
  return out;
}

/** One split leg derived from an assignment — shaped like the Split modal's own `SplitResultEntry`
 *  so it slots straight into `splitOverride` as a split BY AMOUNT. */
export interface AssignedSplitEntry {
  userId: string;
  expenseAmountMinor: number;
  splitEnabled: boolean;
  splitType: 'Amount';
  /** MAJOR units (the wire `value` of an Amount split). */
  amount: number;
}

/**
 * Turn per-member assigned totals into a split-by-amount over `memberIds` (one leg per group
 * member, in that order) whose ENABLED legs sum EXACTLY to `totalMinor`. Members with nothing
 * assigned are present but disabled (0). The shares are scaled proportionally onto the total —
 * a no-op when they already add up to it (the normal 'full' case), and what keeps the split
 * saveable when they don't (an 'each' assignment, or rounding). Empty when nothing is assigned.
 */
export function assignedSharesToSplit(
  shares: Readonly<Record<string, AssignedShare>>,
  memberIds: readonly string[],
  totalMinor: number,
): AssignedSplitEntry[] {
  const raw = memberIds.map((id) => Math.round((shares[id]?.total ?? 0) * 100));
  const enabled = raw.map((v) => v > 0);
  const rawSum = raw.reduce((acc, v, i) => acc + (enabled[i] ? v : 0), 0);
  if (rawSum <= 0 || totalMinor <= 0) return [];
  const legs = convertPartsMinor(raw, totalMinor / rawSum, totalMinor, enabled);
  return memberIds.map((id, i) => {
    const isEnabled = enabled[i] ?? false;
    const minor = isEnabled ? (legs[i] ?? 0) : 0;
    return {
      userId: id,
      expenseAmountMinor: minor,
      splitEnabled: isEnabled,
      splitType: 'Amount',
      amount: Number((minor / 100).toFixed(2)),
    };
  });
}
